/**
 * <summary>
 * "StoreCmsDetails" component shows the store details which are visible to customer form with breadcrumbs and language slider.
 * </summary>
 */

import { observer } from "mobx-react";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { ImageTypeEnum } from "../../../constants/enums/image-type-enum";
import { useStore } from "../../../contexts/store-provider";
import { getStoreIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import AddPopupHOC from "../../../shared-components/popup/add/add-popup-hoc";
import AddCmsDetails from "./components/add-edit-cms-details";
import StoreCmsImages from "./components/store-cms-images";
import StoreCmsWorkingDays from "./components/store-cms-working-days";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";

const StoreCmsDetails = (props: any) => {
  const { smrStore, storeCmsStore } = useStore();
  const {
    resetAddMedicalRecordImageState,
    GetCMSImageByStoreIdImageTypeService,
    updateMedicalRecordImageState,
    addMedicalRecordImageState,
    resetUpdateMedicalRecordImageState,
    deleteMedicalRecordImageState,
    resetDeleteMedicalRecordImageState,
  } = smrStore;
  const {
    GetStoreCmsByIdService,
    AddStoreCmsService,
    UpdateStoreCmsService,
    resetAddUpdateStoreCmsState,
    resetGetStoreCmsState,
    store,
    cmsStoreState,
    addUpdateStoreCmsState
  } = storeCmsStore;

  const storeId = getStoreIdFromLocalStorage();
  const openAddDetailsHandler = () => {
    props.addToggleHandler();
  };

  useEffect(() => {
    GetStoreCmsByIdService(storeId);
    GetCMSImageByStoreIdImageTypeService(
      getStoreIdFromLocalStorage(),
      ImageTypeEnum.StoreCMS
    );
  }, []);

  useEffect(() => {
    if (addMedicalRecordImageState.success) {
      GetCMSImageByStoreIdImageTypeService(
        getStoreIdFromLocalStorage(),
        ImageTypeEnum.StoreCMS
      );
      resetAddMedicalRecordImageState();
    }
  }, [addMedicalRecordImageState.success]);

  useEffect(() => {
    if (updateMedicalRecordImageState.success) {
      GetCMSImageByStoreIdImageTypeService(
        getStoreIdFromLocalStorage(),
        ImageTypeEnum.StoreCMS
      );
      resetUpdateMedicalRecordImageState();
    }
  }, [updateMedicalRecordImageState.success]);

  useEffect(() => {
    if (deleteMedicalRecordImageState.success) {
      GetCMSImageByStoreIdImageTypeService(
        getStoreIdFromLocalStorage(),
        ImageTypeEnum.StoreCMS
      );
      resetDeleteMedicalRecordImageState();
    }
  }, [deleteMedicalRecordImageState.success]);

  return (
    <>
      {props.showAddPopup && (
        <AddCmsDetails
          modalClosed={props.addToggleHandler}
          id={props.id}
          storeId={storeId}
        />
      )}
      {deleteMedicalRecordImageState.inProgress ||
      updateMedicalRecordImageState.inProgress ||
      addMedicalRecordImageState.inProgress ||
      addUpdateStoreCmsState.inProgress ||
      cmsStoreState.inProgress ? (
        <FormLoader
          loading={
            deleteMedicalRecordImageState.inProgress ||
            updateMedicalRecordImageState.inProgress ||
            addMedicalRecordImageState.inProgress ||
            addUpdateStoreCmsState.inProgress ||
            cmsStoreState.inProgress
          }
        />
      ) : null}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="store_details" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="various_settings" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="store_details" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container-fluid mt-4 p-0">
        <div className="row">
          <div className="col-xxl-9 col-xl-8 col-lg-7 col-md-12 col-sm-12 mb-3">
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">
                    <FormattedMessage id="store_cms_details" />
                  </h4>
                  <button
                    className="btn btn-outline-primary"
                    onClick={openAddDetailsHandler}
                  >
                    <FormattedMessage id="edit" />
                  </button>
                </div>
                {/* Store CMS Details Content Goes Here */}
                {
                  storeCmsStore.store && 
                  <div dangerouslySetInnerHTML={{ __html: storeCmsStore.store?.StoreDetails }} />
                }
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">
                    <FormattedMessage id="map_url" />
                  </h4>
                </div>
                {/* Store CMS Details Content Goes Here */}
                <p>{storeCmsStore.store?.StoreMapURL}</p>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">
                    <FormattedMessage id="access_directions" />
                  </h4>
                </div>
                {/* Store CMS Details Content Goes Here */}
                <p>{storeCmsStore.store?.AccessInformation}</p>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">
                    <FormattedMessage id="message" />
                  </h4>
                </div>
                {/* Store CMS Details Content Goes Here */}
                <p>{storeCmsStore.store?.StorePhoneMessage}</p>
              </div>
            </div>
          </div>

          <div className="col-xxl-3 col-xl-4 col-lg-5 col-md-12 col-sm-12 mb-3">
            <StoreCmsWorkingDays storeDetails={storeCmsStore.cmsStoreDetails} />
          </div>

          <div className="col-12">
            <StoreCmsImages storeId={getStoreIdFromLocalStorage()} />
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPopupHOC(observer(StoreCmsDetails));
